/**
 * Datasource remoto Reminders: abstração + implementação Firestore.
 * Recebe Firestore por injeção (clean architecture); se não passado, usa getFirestoreDb().
 */

import type { Firestore } from 'firebase/firestore';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { getFirestoreDb } from '@/core/services';
import { normalizeReminderFields, Reminder, ReminderRef, reminderSchema } from '@/core/domain';

const LOG_PREFIX = '[Reminders][Firestore]';

function remindersDebug(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(LOG_PREFIX, ...args);
  }
}

export interface ReminderRemoteDataSource {
  getById(id: string): Promise<Reminder | null>;
  getAllByUserId(userId: string): Promise<Reminder[]>;
  getAll(userId: string): Promise<Reminder[]>;
  create(reminder: Reminder): Promise<Reminder>;
  update(id: string, reminder: Partial<Reminder>): Promise<Reminder>;
  delete(id: string): Promise<void>;
}

function toReminder(id: string, data: Record<string, unknown>): Reminder {
  const payload = normalizeReminderFields(id, data);
  const parsed = reminderSchema.safeParse(payload);
  if (!parsed.success) {
    const flat = parsed.error.flatten();
    const zodFormat = parsed.error.format();
    // eslint-disable-next-line no-console
    console.error(LOG_PREFIX, 'reminderSchema.safeParse failed', {
      docId: id,
      zodFlatten: flat,
      zodFormat,
      rawKeys: Object.keys(data),
      statusRaw: data.status,
      sentAtRaw: data.sent_at,
    });
    remindersDebug('Full raw document (dev)', payload);
    throw new Error('Invalid reminder from Firestore');
  }
  return parsed.data;
}

export class ReminderFirestoreDataSource implements ReminderRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  async getById(id: string): Promise<Reminder | null> {
    remindersDebug('getById', { id });
    const ref = doc(this.getDb(), ReminderRef.collection, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      remindersDebug('getById: document missing', { id });
      return null;
    }
    return toReminder(snap.id, snap.data() as Record<string, unknown>);
  }

  async getAllByUserId(userId: string): Promise<Reminder[]> {
    remindersDebug('getAllByUserId: query start', { userId });
    const ref = collection(this.getDb(), ReminderRef.collection);
    const q = query(ref, where('user_id', '==', userId));
    const snap = await getDocs(q);
    remindersDebug('getAllByUserId: query result', {
      userId,
      docCount: snap.docs.length,
      docIds: snap.docs.map((d) => d.id),
    });
    return snap.docs.map((d) => toReminder(d.id, d.data() as Record<string, unknown>));
  }

  async getAll(userId: string): Promise<Reminder[]> {
    return this.getAllByUserId(userId);
  }

  async create(reminder: Reminder): Promise<Reminder> {
    const ref = doc(this.getDb(), ReminderRef.collection, reminder.id);
    await setDoc(ref, reminder);
    return reminder;
  }

  async update(id: string, reminder: Partial<Reminder>): Promise<Reminder> {
    const ref = doc(this.getDb(), ReminderRef.collection, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Reminder not found');
    const current = toReminder(snap.id, snap.data() as Record<string, unknown>);
    const updated = { ...current, ...reminder, id };
    await setDoc(ref, updated);
    return updated;
  }

  async delete(id: string): Promise<void> {
    const ref = doc(this.getDb(), ReminderRef.collection, id);
    await deleteDoc(ref);
  }
}
